import { defineStore } from 'pinia';
import { CancelInsuranceFormService as _cancelInsurance } from '@/services/store/cancel-insurance-form.service';
import reasonsQueryGraphQL from './cancel-insurance-reasons.query.graphql';
import { inject, ref } from 'vue';
import { useAnalyticsStore } from '../analytics/analytics.state';
import { FormEvents } from '@/enums/form-events.enum';
import { IFormAnalytics } from '@/interfaces/forms/analytics-interface';
import { JP_EXCEPTION, JP_GENERIC_CODE } from '@/constants/jeopardy-codes.const';
import {
  CancelInsuranceFormData,
  CancelInsuranceReason,
  InsuranceProduct,
  IReviewSummary,
  IServiceResponseError,
  ISitecoreItem,
  ISitecoreReviewSummaryItem,
} from '@/interfaces';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { format, isDate, parseISO } from 'date-fns';
import { replaceBookmark, replaceBookmarks } from '@/utils/bookmark.replace';
import { useFormStore } from '../form/formStore';
import { CancelInsuranceDisplayRules } from '@/utils/display-rules/cancel-insurance-rules';
import { getPolicySubTitle, getPolicyTitle } from '@/utils/self-service-rules/InsurancePolicyStatusRules';
import { useI18n } from 'vue-i18n';
import {
  Bookmarks,
  PolicyStepState,
  reasonCodes,
} from '@/forms/my-racq/insurance/cancel-insurance/mappings/cancel-insurance-mappings';
import { IHighlight } from '@/interfaces/forms/highlights-interface';
import { ApolloClient } from '@apollo/client';
import { DefaultApolloClient } from '@vue/apollo-composable';
import GraphQLClientFactory from '@/lib/GraphQLClientFactory';
import config from '@/temp/config';
import { toUUID } from '@/utils/common-helper';
import { toBool } from '@/utils/convert';

export const useCancelInsuranceStore = defineStore('CancelInsurance', () => {
  const analyticsStore = useAnalyticsStore();
  const formStore = useFormStore();
  const translation = useI18n();

  // ----- apollo client (inject first, fallback to factory)
  const injected = inject<ApolloClient<any> | null>(DefaultApolloClient, null);
  const client: ApolloClient<any> = injected ?? GraphQLClientFactory(config.graphQLEndpoint, true);

  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const policyNumber = ref<string>();
  const currentPolicy = ref<InsuranceProduct>();
  const coverageDetails = ref<string>();
  const currentDateTime = ref<string>();
  const defaultCancellationDate = ref<string>();
  const insuranceIndexNumber = ref<number>();
  const insurancePolicyType = ref<string>();
  const insuranceType = ref<string>();
  const maxCancellationDate = ref<string>();
  const isCoolingPeriod = ref<boolean>();
  const email = ref<string>();
  const errors = ref<IServiceResponseError[]>([]);
  const model = ref<CancelInsuranceFormData>({});
  const policySubStep = ref(PolicyStepState.DATE);
  const insuranceTypeDisplayName = ref<string>();
  const insuranceTypeDisplayDetails = ref<string>();
  const cancellationReasons = ref<CancelInsuranceReason[]>([]);

  function showPolicyDateStep() {
    policySubStep.value = PolicyStepState.DATE;
  }

  function showPolicyReasonStep() {
    policySubStep.value = PolicyStepState.REASON;
  }

  function mapCancellationReason(item: any): CancelInsuranceReason | null {
    if (item.__typename !== 'CancellationReasons') return null;

    return {
      id: item.id,
      name: item.name,
      insuranceType: item.insuranceType?.rendered ?? '',
      reasonCode: item.reasonCode?.value ?? '',
      listValue: item.listValue?.value ?? '',
      listLabel: item.listLabel?.value ?? '',
      listAsRadio: item.listAsRadio?.value ?? '',
      requiredErrorMessage: item.requiredErrorMessage?.value ?? '',
      additionalCommentsRequired: item.additionalCommentsRequired?.value === '1',
      triggerChangeVehicle: item.triggerChangeVehicle?.value === '1',
      children: (item.children || []).map(mapCancellationReason).filter(Boolean),
    };
  }

  async function getCancellationReasons() {
    const graphQLPath = '/sitecore/content/RACQFormsGroup/RACQForms20/Data/InsuranceCancellationReasons/';

    const { data } = await client.query({
      query: reasonsQueryGraphQL,
      variables: { path: graphQLPath },
    });

    const reasons = (data?.item?.children || []).map(mapCancellationReason).filter(Boolean);

    cancellationReasons.value = reasons;
  }

  async function fetchPolicy() {
    const params = new URLSearchParams(location.search);
    policyNumber.value = params.get('policyNumber') ?? '';
    if (!policyNumber.value) throw JP_GENERIC_CODE;
    const response = await _cancelInsurance.fetchPolicy(policyNumber.value);
    errors.value = response.errors;
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE;
    }
    currentPolicy.value = response.insurancePolicy;
    coverageDetails.value = response.coverageDetails;
    currentDateTime.value = response.currentDateTime;
    defaultCancellationDate.value = response.defaultCancellationDate;
    insuranceIndexNumber.value = response.insuranceIndexNumber;
    insurancePolicyType.value = response.insurancePolicyType;
    insuranceType.value = response.insuranceType;
    maxCancellationDate.value = response.maxCancellationDate;
    isCoolingPeriod.value = response.isCoolingPeriod;
    email.value = response.emailAddress;

    const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;
    insuranceTypeDisplayName.value = getPolicyTitle(dictionary, currentPolicy.value, false);
    insuranceTypeDisplayDetails.value = getPolicySubTitle(currentPolicy.value);
  }

  async function isCancellationReasonEligible(payload: {
    cancellationReasonId: string;
    subCancellationReasonId?: string;
  }) {
    const res = await _cancelInsurance.isInsuranceEligibleForCancellation(policyNumber.value!, payload);

    if (!res.isEligible) {
      throw res.jpCode || JP_EXCEPTION;
    }
    return res;
  }

  async function isCancellationDateEligible(payload: { cancellationDate: string }) {
    const res = await _cancelInsurance.isCancellationDateEligible({ ...payload, policyNumber: policyNumber.value! });

    if (!res.isEligible) {
      throw res.jpCode || JP_EXCEPTION;
    }
    return res;
  }

  async function canceInsurance() {
    const payload = {
      policyNumber: policyNumber.value!,
      cancellationReason: model.value?.reason,
      subCancellationReason: model.value?.subReason,
      cancellationReasonId: model.value?.reasonCode,
      subCancellationReasonId: model.value?.subReasonCode,
      cancellationDate: model.value?.cancellationDate,
      refundPaymentDetails: {
        accountHolderName: model.value?.accountHoldername,
        bsbNumber: model.value?.bsbNumber,
        accountNumber: model.value?.accountNumber,
      },
      intendingToMakeClaim: model.value?.intendingToMakeClaim,
    };

    const res = await _cancelInsurance.cancelInsurance(payload);
    if (!res.workOrderId) {
      throw JP_EXCEPTION;
    }
    return res;
  }

  const mapPolicyDateDetails = (formData?: Record<string, any>) => {
    if (formData?.cancellationDate) {
      const utcDate = isDate(formData.cancellationDate)
        ? formData.cancellationDate
        : parseISO(formData.cancellationDate); // parse UTC string
      model.value!.cancellationDate = format(utcDate, 'yyyy-MM-dd'); // local date string
    }
    model.value!.isCoolingPeriod = isCoolingPeriod.value;
    if (isCoolingPeriod.value) {
      const selectedReason = cancellationReasons.value.find(
        (x) => toUUID(x.id) == reasonCodes.freeLookReasonSitecoreId
      );
      model.value!.reason = selectedReason?.listValue;
      model.value!.reasonCode = toUUID(selectedReason?.id || '');
      model.value!.subReasonsFields = ['Cancelling during the cooling-off period'];
    }
    model.value!.intendingToMakeClaim = toBool(formData?.hasClaim);

    return model.value;
  };

  const mapPolicyReasonDetails = (formData?: Record<string, any>) => {
    const selectedReason = cancellationReasons.value.find((x) => x.id == formData?.reason);
    model.value!.reason = selectedReason?.listValue || '';
    model.value!.reasonCode = toUUID(formData?.reason);

    const selectedSubReasonSecond = selectedReason?.children?.find((x) => x.id == formData?.subReasonSecond);
    const selectedSubReasonThird = selectedSubReasonSecond?.children?.find((x) => x.id == formData?.subReasonThird);
    const selectedSubReasonFourth = selectedSubReasonThird?.children?.find((x) => x.id == formData?.subReasonFourth);

    model.value!.subReason =
      selectedSubReasonFourth?.listValue ||
      selectedSubReasonThird?.listValue ||
      selectedSubReasonSecond?.listValue ||
      '';
    model.value!.subReasonCode = toUUID(
      formData?.subReasonFourth || formData?.subReasonThird || formData?.subReasonSecond
    );

    model.value!.subReasonsFields = [
      selectedSubReasonSecond && `${selectedReason?.listLabel} - ${selectedSubReasonSecond?.listValue}`,
      selectedSubReasonThird && `${selectedSubReasonSecond?.listLabel} - ${selectedSubReasonThird?.listValue}`,
      selectedSubReasonFourth && `${selectedSubReasonThird?.listLabel} - ${selectedSubReasonFourth?.listValue}`,
    ].filter(Boolean) as string[];

    return model.value;
  };

  const mapRefundDetails = (formData?: Record<string, any>) => {
    model.value!.accountHoldername = formData?.accountName;
    model.value!.accountNumber = formData?.accountNumber;
    model.value!.bsbNumber = formData?.accountBsb;
    model.value!.requestedBy = formData?.accountName;
    return model.value;
  };

  async function getReviewSummaryModel(summaryFields: any): Promise<IReviewSummary[]> {
    const summaryFieldsResult: IReviewSummary[] = [];

    const policyDetailsObj = {
      insuranceTypeDisplayName: insuranceTypeDisplayName.value,
      insuranceTypeDisplayDetails: insuranceTypeDisplayDetails.value,
      policyNumber: policyNumber.value,
      cancellationDate: format(model.value?.cancellationDate ?? '', STANDARD_DATE_FORMAT),
      cancellationReason: model.value?.reason,
    };
    const refundDetailsObj = {
      accountName: model.value?.accountHoldername,
      accountNumber: model.value?.accountNumber,
      accountBsb: model.value?.bsbNumber,
    };

    summaryFieldsResult.push(replaceBookmarks(summaryFields[0], policyDetailsObj));
    summaryFieldsResult.push(replaceBookmarks(summaryFields[1], refundDetailsObj));

    return summaryFieldsResult.map((field: any) => ({
      ...field,
      items: field.items
        .filter((item: ISitecoreReviewSummaryItem) => {
          let showItem = true;
          if (item.fields.displayRules?.length) {
            showItem = executeDisplayRules(item.fields.displayRules, model.value);
          }
          return showItem;
        })
        .map((item: ISitecoreReviewSummaryItem) => {
          return {
            ...item,
            ...item.fields,
            editGoToStep: formStore.getStep(item.fields.editStep.value)?.stepIndex,
            showEdit: item.fields.editStep?.value ?? false,
          };
        }),
    }));
  }

  function executeDisplayRules(rules: ISitecoreItem[], details: CancelInsuranceFormData): boolean {
    if (rules && rules.length > 0) {
      const anyRuleIsTrue = rules.every(function (rule) {
        if ((CancelInsuranceDisplayRules as any)[rule.name]) {
          return (CancelInsuranceDisplayRules as any)[rule.name](details);
        }
      });
      return anyRuleIsTrue;
    }
    return true;
  }

  function getHighlightsModel(formInputs: any) {
    console.log(formInputs);
    const highlights = [] as IHighlight[];
    highlights.push({
      content: replaceBookmark(
        Bookmarks.insuranceTypeDisplayName,
        formInputs.highlightsStep1.label.value!,
        insuranceTypeDisplayName.value!
      ),
    });
    highlights.push({
      content: replaceBookmark(Bookmarks.email, formInputs.highlightsStep2.label.value!, email.value!),
    });
    highlights.push({ content: formInputs.highlightsStep3.label.value! });

    return highlights;
  }

  function mapInsuranceTypeDisplay(content: string) {
    let res = replaceBookmark(Bookmarks.insuranceTypeDisplayName, content, insuranceTypeDisplayName.value!);
    res = replaceBookmark(Bookmarks.insuranceTypeDisplayDetails, res, insuranceTypeDisplayDetails.value!);

    return res;
  }

  /**
   * Push data to analytics
   *
   * @param options
   */
  const pushAnalytics = (options: { event: FormEvents; form: any }) => {
    const analyticsModel: IFormAnalytics = {
      event: options.event,
      form: {
        productName: insuranceTypeDisplayName.value,
        ...(model.value.subReasonsFields && {
          field: model.value.subReasonsFields.join(', '),
        }),
        ...(model.value.cancellationDate && {
          termEndDate: model.value.cancellationDate,
        }),
        ...(model.value.reason && {
          enquiryType: model.value.reason,
        }),
        ...options.form,
      },
    };

    analyticsStore.pushAnalytics(analyticsModel);
  };

  return {
    policyNumber,
    currentPolicy,
    insurancePolicyType,
    insuranceTypeDisplayName,
    currentDateTime,
    maxCancellationDate,
    defaultCancellationDate,
    isCoolingPeriod,
    policySubStep,
    cancellationReasons,
    model,
    showPolicyDateStep,
    showPolicyReasonStep,
    fetchPolicy,
    canceInsurance,
    getCancellationReasons,
    isCancellationReasonEligible,
    isCancellationDateEligible,
    mapPolicyDateDetails,
    mapPolicyReasonDetails,
    mapRefundDetails,
    getReviewSummaryModel,
    getHighlightsModel,
    mapInsuranceTypeDisplay,
    pushAnalytics,
  };
});
